import { BCMSRecord, BCMSStateManagerCRUD, BrandingStyle, NavLink, Outlet, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import EDEStatementsPageContainer from "components/EDEStatements/shared/containers/EDEStatementsPageContainer";
import EDEStatementsPageContentView from "components/EDEStatements/shared/views/EDEStatementsPageContentView";
import { CenterContentContainer } from "components/shared";
import Classes, { Account, CheckingAccount, CODAccount, LoanAccount, LOCAccount, SavingsAccount, TransferTransaction, User } from "model";
import React from "react";
import "./styles.css";
import "./mobile.css";


/**
 * @function EdeEditUserSettingsPage
 * @return {React.Component}
 * @exported true
 * @description The ede "Edit User Settings" page.
 */
export default function EdeEditUserSettingsPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);
    const model = useModel();
    const { branding } = useModel();
    const data = { ...getDefaultPageData(branding, model.user), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
    const state = { data, ui };
    const accountTypeClassMap = { 
      "checking": CheckingAccount, 
      "savings": SavingsAccount, 
      "loan": LoanAccount, 
      "loc": LOCAccount, 
      "cod": CODAccount 
    };

    const objsAreEqual = (instance1, instance2, ignoreProps) => {
      let areEqual = true;

      instance1.forEach((item, index) => {
        const _instance1Record = { ...item.record };
        const _instance2Record = { ...instance2[index].record };
        
        if(JSON.stringify(_instance1Record) !== JSON.stringify(_instance2Record)) {
          areEqual = false;
        }
        return item;
      });

      return areEqual;
    };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onDeliveryOptionChange: (prop, accountID, deliveryOption) => evt => {

          const updatedAccounts = state.data.accounts.map(account => {
            let updateAccount = undefined;

            // Explicitly set the updated prop since "init({ someprop: somval })" 
            // with the account types like "CheckingAccount" dont modify the values.
            if(account.id === accountID) {
              updateAccount = accountTypeClassMap[account.type].init({ ...account.record });
              updateAccount[prop] = deliveryOption.label;
            } else {
              updateAccount = accountTypeClassMap[account.type].init({ ...account.record });
              updateAccount[prop] = account[prop];
            }
            return updateAccount;
          });

          BCMSStateManagerCRUD.init(state)
          .update([ "data", "accounts" ], updatedAccounts)
          .submit(setState);
        },

        didUpdateAccounts: () => {
          return !objsAreEqual(state.data.accounts, state.data.defaultAccounts);
        },

        resetAccounts: () => {
          BCMSStateManagerCRUD.init(state)
          .update([ "data", "accounts" ], getDefaultPageData(branding).accounts)
          .submit(setState);
        }
    });

    return (
        <EDEStatementsPageContainer className="ede-edit-user-settings-page" state={ state } controller={ controller }>
            <PageContent />
        </EDEStatementsPageContainer>
    );
}

export const getDefaultPageData = (branding, user) => {
  const _user = user ? User.init(user) : {};

  // START: This code was copied from component "StatementPreferencePage".
  const options = branding ? branding.options : {};
    const accountTypeMap = {
      "checking": "Checking",
      "savings": "Savings",
      "loc": "Line of Credit",
      "cod": "Certificate of Deposit",
      "loan": "Loan"
    };
  const deliveryOptionsBranding = options.delivery_options_abi22 === "undefined" || options.delivery_options_abi22 === undefined
  ? "Paper,Electronic"
  : options.delivery_options_abi22;
  let deliveryOptions = deliveryOptionsBranding.split(",");

  if(deliveryOptions.length === 1) {
      deliveryOptions = [ "Other", ...deliveryOptions ];
  }

  /* Static delivery options until we get a branding option for ede statements. */
  deliveryOptions = [ "e-Statement", "Paper Statement" ].reduce((acc, option, index) => {
      acc.push({
          label: option,
          index: index,
          //isDemoDefaultOption: option === "Other" && index === 0
      });
      return acc;
  }, []);
  
  /* Uncomment to make delivery options dynamic with branding. */
  /*deliveryOptions = deliveryOptions.reduce((acc, option, index) => {
      acc.push({
          label: option,
          index: index,
          isDemoDefaultOption: option === "Other" && index === 0
      });
      return acc;
  }, []);*/

  const deliveryOptionsHasPaper = deliveryOptions.filter(item => item.label === "Paper").length > 0;
  const deliveryOptionsPaperIndex = deliveryOptions.findIndex(item => item.label === "Paper");

  /* Static delivery options until we get a branding option for ede statements. */
  const accounts = Account.initAll().reduce((acc, item, index) => {
      item.statementPreference = deliveryOptions[0].label;
      item.currIndex = index;
      acc.push(item);
      return acc;
  }, []);

  /* Uncomment to make delivery options dynamic with branding. */
  /*const accounts = Account.initAll().reduce((acc, item, index) => {
      if(index === 0) {
          if(deliveryOptionsHasPaper) {
              item.statementPreference = "Paper";
          } else {
              item.statementPreference = deliveryOptions[0].label;
          }
      } else {
          if(deliveryOptionsHasPaper) {
              if(deliveryOptionsPaperIndex > 0) {
                  item.statementPreference = deliveryOptions[0].label;
              } else {
                  item.statementPreference = deliveryOptions[1].label;
              }
          } else {
              item.statementPreference = deliveryOptions[1].label;
          }
      }

      item.currIndex = index;
      acc.push(item);
      return acc;
  }, []);*/
  // END: This code was copied from component "StatementPreferencePage".  

  return {
    accounts,
    accountTypeMap,
    defaultAccounts: accounts,
    deliveryOptions,
    contactInfo: _user.email,
    scheduledTransfers: TransferTransaction.initAll().filter(item => item.id !== undefined).filter(transfer => transfer.status === "scheduled", []),
  };
};
export const getDefaultPageUI = () => ({
  content: undefined,
  deliveryOptions: { isActive: false },
  email: { isActive: true },
  scheduledTransfers: { isActive: false },
});

function PageContent(props) {
    const { data, ui } = useStateManager();
    const Component = ui.content;

    return (
        <EDEStatementsPageContentView>
          <div className="flex-container flex-vertical page-widget-wrapper">
            <CenterContentContainer>
                <TabsView />
                { ui.content === undefined ? <Outlet /> : <Component /> }
            </CenterContentContainer>
          </div>
        </EDEStatementsPageContentView>
    )
}

function TabsView(props) {
    const { data, ui } = useStateManager();
    const deliveryOptionsActive = ui.content !== undefined && ui.deliveryOptions.isActive ? "active-item" : "";
    const emailActive = ui.content !== undefined && ui.email.isActive ? "active-item" : "";
    const deliveryOptionsClasses = `tab set-width delivery-options ${ deliveryOptionsActive }`;
    const emailClasses = `tab set-width email ${ emailActive }`;
    const deliveryOptionsLink = (
      <NavLink className={`tab set-width delivery-options ${ deliveryOptionsActive }`} activeClassName="active-item" activeRoutes={["/edeEditUserSettings/deliveryOptions"]} to="/edeEditUserSettings/deliveryOptions">
        Delivery Options
      </NavLink>
    );
    const emailLink = (
      <NavLink className={`tab set-width email ${ emailActive }`} activeClassName="active-item" activeRoutes={["/edeEditUserSettings/email"]} to="/edeEditUserSettings/email">
        Email
      </NavLink>
    );

    const setBrandedLink = (activeClass, classes, linkElem) => {
      return activeClass ? linkElem : <BrandingStyle style={ { "color": "links_abi22" } } className={ classes } element={ linkElem } />;
    };

    return(
      <div className="ede-edit-user-settings-tabs-view flex-container">    
      { setBrandedLink(deliveryOptionsActive, deliveryOptionsClasses, deliveryOptionsLink) }
      { setBrandedLink(emailActive, emailClasses, emailLink) }    
      </div>
    );
  }